import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle, Loader2, Building2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const countries = [
  "Nigeria", "Ghana", "Kenya", "Tanzania", "Uganda", "Ethiopia", "South Africa",
  "UAE", "Saudi Arabia", "Oman", "Kuwait", "Qatar", "Bahrain",
  "Bangladesh", "Nepal", "Sri Lanka", "Myanmar", "Afghanistan",
  "United States", "United Kingdom", "Canada", "Australia", "Other"
];

export default function HospitalInquiryModal({ trigger, hospitalName, treatments = [] }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", country: "", treatment: "", message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.country) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/hospital-inquiry`, {
        ...form,
        hospital_name: hospitalName,
        source_page: window.location.href
      });
      setSuccess(true);
      setTimeout(() => { setOpen(false); setSuccess(false); setForm({ name: "", email: "", phone: "", country: "", treatment: "", message: "" }); }, 3000);
    } catch (err) {
      alert("Failed to submit inquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md" data-testid="hospital-inquiry-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Building2 className="h-5 w-5 text-orange-600" />
            Inquire About {hospitalName}
          </DialogTitle>
        </DialogHeader>
        
        {success ? (
          <div className="py-12 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Inquiry Submitted!</h3>
            <p className="text-slate-500">Our team will contact you within 24 hours.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Full Name *</Label>
                <Input 
                  value={form.name} 
                  onChange={e => setForm({...form, name: e.target.value})}
                  placeholder="Your name"
                  required
                  data-testid="hospital-inquiry-name"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Phone *</Label>
                <Input 
                  value={form.phone} 
                  onChange={e => setForm({...form, phone: e.target.value})}
                  placeholder="+1 234 567 8900"
                  required
                  data-testid="hospital-inquiry-phone"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-xs text-slate-500">Email *</Label>
              <Input 
                type="email"
                value={form.email} 
                onChange={e => setForm({...form, email: e.target.value})}
                placeholder="your@email.com"
                required
                data-testid="hospital-inquiry-email"
              />
            </div>
            
            <div>
              <Label className="text-xs text-slate-500">Country *</Label>
              <Select value={form.country} onValueChange={v => setForm({...form, country: v})}>
                <SelectTrigger data-testid="hospital-inquiry-country">
                  <SelectValue placeholder="Select your country" />
                </SelectTrigger>
                <SelectContent>
                  {countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            
            {treatments.length > 0 && (
              <div>
                <Label className="text-xs text-slate-500">Treatment Interest</Label>
                <Select value={form.treatment} onValueChange={v => setForm({...form, treatment: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select treatment (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {treatments.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label className="text-xs text-slate-500">Message (Optional)</Label>
              <Textarea 
                value={form.message}
                onChange={e => setForm({...form, message: e.target.value})}
                placeholder="Tell us about your medical needs..."
                rows={3}
                data-testid="hospital-inquiry-message"
              />
            </div>
            
            <Button 
              type="submit" 
              disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white rounded-full py-6 font-semibold"
              data-testid="hospital-inquiry-submit"
            >
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting...</> : "Submit Inquiry"}
            </Button>
            
            <p className="text-xs text-slate-400 text-center">
              By submitting, you agree to be contacted by our medical coordination team.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
